import { ArrowDownIcon } from "@phosphor-icons/react/dist/csr/ArrowDown";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
} from "@volca/ui/components/ui/empty";
import { Stack } from "@volca/ui/components/ui/stack";
import { emptyStateVariants } from "@volca/ui/lib/empty-state";
import { cn } from "@volca/ui/lib/utils";
import type {
  EnrichedMessagePage,
  EnrichedTurnActivity,
} from "@/payroll/editor/types";
import { OwnerMessage } from "./owner-message";
import { TranscriptMessage } from "./transcript-message";
import { TranscriptSkeleton } from "./transcript-skeleton";
import { TurnActivity } from "./turn-activity";
import type { PostedOutgoing } from "./use-outgoing";

const FADE =
  "pointer-events-none absolute inset-x-0 z-10 h-6 from-surface-raised to-transparent";

// How much has to be below the fold before the bottom fade means anything.
// Fractional layout leaves a pixel of slack at the true bottom, so a stricter
// test leaves the fade lit over the last line with nothing under it.
const EDGE = 2;
// Close enough to the foot that new content should carry the reader with it.
const FOLLOW = 80;

export const EditorTranscript = ({
  page,
  loading,
  isSending,
  onReply,
  onWriteOwn,
  onResend,
  canResend,
  canRetry,
  posted,
  onRetry,
  onDiscard,
  queueCount,
  activity,
  active,
  sending,
  stopping,
}: {
  page?: EnrichedMessagePage;
  /** Nothing is known yet. Distinct from a conversation with no messages. */
  loading: boolean;
  isSending: boolean;
  onReply: (value: string) => void;
  onWriteOwn: () => void;
  onResend: (request: string) => void;
  /** Sending is possible at all: not that this message is the newest one. */
  canResend: boolean;
  canRetry: boolean;
  /** Sent, not yet in the transcript. Rendered as ordinary messages. */
  posted: PostedOutgoing[];
  onRetry: (id: string) => void;
  onDiscard: (id: string) => void;
  /** Queue rows grow the composer, which shortens this pane under the reader. */
  queueCount: number;
  activity?: EnrichedTurnActivity | null;
  active: boolean;
  sending: boolean;
  stopping: boolean;
}) => {
  const scroll = useRef<HTMLDivElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const nearBottom = useRef(true);
  // Whether anything has scrolled under the pane's top edge yet. The fade only
  // has something to do once it has.
  const [scrolled, setScrolled] = useState(false);
  // And whether anything is still below the bottom edge. The same fade, the
  // other way up: without it the last line runs into the composer with nothing
  // to say the transcript continues under it.
  const [more, setMore] = useState(false);
  const messages = page?.messages ?? [];
  const initialMessages = useRef<Set<string> | null>(null);
  if (page && !initialMessages.current)
    initialMessages.current = new Set(messages.map((message) => message.id));
  // One place that reads the viewport. It was written out three times, once per
  // caller, and they had already drifted apart by a threshold.
  const sync = useCallback(() => {
    const node = scroll.current;
    if (!node) return;
    const remaining = node.scrollHeight - node.scrollTop - node.clientHeight;
    nearBottom.current = remaining < FOLLOW;
    setScrolled(node.scrollTop > 0);
    setMore(remaining > EDGE);
  }, []);
  // `scroll` does not bubble, so the listener goes on the viewport itself.
  //
  // It depends on `loading` because of where the ref comes from. While the
  // viewport and no ref. With an empty dependency list the effect ran once
  // against that null, bailed, and never ran again: the listener was never
  // attached for the whole life of the pane and neither edge fade ever
  // appeared, however far the reader scrolled.
  useEffect(() => {
    const node = scroll.current;
    if (!node) return;
    sync();
    node.addEventListener("scroll", sync);
    return () => node.removeEventListener("scroll", sync);
  }, [loading, sync]);
  // The transcript grows while a turn runs, a step at a time, and none of that
  // touches the props below. A reader sitting at the foot was left behind by
  // every new line: the content got taller under them, so the bottom fade lit
  // over text that had just appeared and nothing scrolled to show it. Watching
  // the content itself is the only thing that sees those growths.
  useEffect(() => {
    const node = content.current;
    const viewport = scroll.current;
    if (!node || !viewport) return;
    const observer = new ResizeObserver(() => {
      if (nearBottom.current) viewport.scrollTop = viewport.scrollHeight;
      sync();
    });
    observer.observe(node);
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [loading, sync]);
  useEffect(() => {
    if (isSending) nearBottom.current = true;
    if (nearBottom.current && scroll.current)
      scroll.current.scrollTop = scroll.current.scrollHeight;
    // Sending auto-scrolls, which is a scroll the listener never sees.
    sync();
  }, [messages.length, isSending, posted.length, queueCount, sync]);
  if (loading) return <TranscriptSkeleton />;
  return (
    <div className="relative min-h-0 flex-1 text-sm">
      {/* Show each fade only when more transcript exists beyond that edge. */}
      {scrolled && (
        <div aria-hidden className={cn(FADE, "top-0 bg-linear-to-b")} />
      )}
      {/* The same treatment at the foot of the pane, so the transcript fades
          under the composer rather than being cut off by it. */}
      {more && (
        <div aria-hidden className={cn(FADE, "bottom-0 bg-linear-to-t")} />
      )}
      <div
        ref={scroll}
        className="quiet-scrollbar h-full overflow-y-auto overscroll-contain"
      >
        <Stack
          gap={0}
          align="stretch"
          direction="column"
          ref={content}
          className="min-h-full gap-6 px-4 pt-5 pb-6"
        >
          {/* The invitation, only once there is nothing to invite onto. It
              used to render whenever the list was empty, which includes every
              reload before the first answer comes back, so a thread with five
              messages opened by asking what should change. */}
          {!messages.length && !loading && (
            <Empty className={emptyStateVariants({ layout: "inline" })}>
              <EmptyHeader>
                <EmptyDescription>
                  What should change? Describe the rule and which employees it
                  affects.
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          )}
          {messages.map((message) => (
            <TranscriptMessage
              key={
                message.role === "assistant" && message.request_id
                  ? `reply:${message.request_id}`
                  : message.id
              }
              animateReply={
                !!message.streaming || !initialMessages.current?.has(message.id)
              }
              message={message}
              stopping={stopping && message.id === activity?.requestId}
              disabled={message.request_id !== page?.latest_request?.id}
              onReply={onReply}
              onWriteOwn={onWriteOwn}
              onResend={onResend}
              canResend={canResend}
            />
          ))}
          {/* Sent, and in the conversation where it belongs. Nothing marks the
              ordinary case: a message on its way looks like a message. Only a
              failure speaks up, on the bubble itself, with the two things worth
              offering — try again, or take the words back. */}
          {posted.map((item) => (
            // The same wrapper a delivered message gets, so the bubble's parent
            // chain is identical and nothing shifts when the real one arrives.
            <div key={item.id} className="min-w-0 wrap-break-word">
              {/* A message the server saved is already in the list above. Only
                  its failure row is missing, so drawing the bubble again would
                  show the reader their words twice. */}
              {!item.saved && (
                <OwnerMessage attachments={item.attachments}>
                  {item.message}
                </OwnerMessage>
              )}
              {/* Try again, or throw it away. Edit used to be here too, and it
                  worked by pulling the words back into the composer, which
                  threw away whatever was already typed there. It could not do
                  what it looked like it did either: a saved request keeps its
                  id, and the server refuses that id carrying different words.
                  Discard then write is the honest version of the same move. */}
              {item.failed && (
                <Stack
                  gap={0}
                  align="stretch"
                  direction="row"
                  role="alert"
                  className="mt-1 ml-auto flex items-center gap-2 text-xs"
                >
                  <span>
                    {item.saved ? "Saved, but it did not start." : "Not sent."}
                  </span>
                  <Button
                    size="xs"
                    variant="ghost"
                    className="h-auto p-0"
                    disabled={!canRetry}
                    onClick={() => onRetry(item.id)}
                  >
                    Try again
                  </Button>
                  <Button
                    size="xs"
                    variant="ghost"
                    className="h-auto p-0"
                    onClick={() => onDiscard(item.id)}
                  >
                    Discard
                  </Button>
                </Stack>
              )}
            </div>
          ))}
          {((sending && !active) ||
            !messages.some(
              (message) => message.id === activity?.requestId,
            )) && (
            <TurnActivity
              activity={sending && !active ? null : activity}
              active={active}
              sending={sending}
              stopping={stopping}
            />
          )}
        </Stack>
      </div>
      {more && (
        <Button
          type="button"
          size="xs"
          variant="outline"
          aria-label="Jump to latest message"
          className="absolute bottom-2 left-1/2 z-20 -translate-x-1/2 rounded-full bg-surface-raised shadow-raised"
          onClick={() => {
            nearBottom.current = true;
            if (scroll.current)
              scroll.current.scrollTop = scroll.current.scrollHeight;
            sync();
          }}
        >
          <ArrowDownIcon aria-hidden /> Latest
        </Button>
      )}
    </div>
  );
};
